<template>
  <ConversationMessageAssistantRenderless
    :message="message"
    :is-streaming="isStreaming"
    v-slot="{ html, isEmpty, functionCalls, isExpanded, toggle, formatResult, meta }">
    <div class="tw:flex tw:flex-col tw:gap-3">
      <div
        v-if="functionCalls.length"
        class="tw:flex tw:flex-col tw:gap-2 tw:rounded-lg tw:border tw:border-[#edeeef] tw:bg-[#F7F7F7] tw:p-3">
        <div class="tw:flex tw:items-center tw:gap-2 tw:text-xs tw:font-medium tw:text-gray-600">
          <IconBrain :size="14" />
          <span>Thinking{{ thinkingDuration(meta) }}</span>
        </div>

        <div v-for="functionCall in functionCalls" :key="functionCall.id">
          <button
            type="button"
            class="tw:flex tw:w-full tw:items-center tw:gap-2 tw:border-0 tw:bg-transparent tw:p-0 tw:text-left tw:text-[13px] tw:text-gray-600 tw:hover:text-gray-900"
            :class="functionCall.status === 'executing' ? 'tw:font-medium tw:text-gray-900' : ''"
            :title="`Technical name: ${functionCall.name}`"
            @click="toggle(functionCall.id)">
            <IconLoader2
              v-if="functionCall.status === 'executing' || functionCall.status === 'pending'"
              class="ap-spin tw:shrink-0 tw:text-primary"
              :size="14" />
            <IconAlertTriangle v-else-if="isFailedCall(functionCall)" class="tw:shrink-0 tw:text-red-600" :size="14" />
            <IconCheck v-else class="tw:shrink-0 tw:text-[#067647]" :size="14" />
            <span class="tw:min-w-0 tw:flex-1 tw:truncate">{{ translateFunctionName(functionCall.name) }}</span>
            <IconChevronDown
              class="tw:shrink-0 tw:text-gray-400 tw:transition-transform"
              :class="isExpanded(functionCall.id) ? 'tw:rotate-180' : ''"
              :size="12" />
          </button>
          <div
            v-if="isFailedCall(functionCall) && !isExpanded(functionCall.id)"
            class="tw:mt-1 tw:pl-6 tw:text-xs tw:text-red-700">
            {{ errorMessageFor(functionCall) }}
          </div>

          <div
            v-if="isExpanded(functionCall.id)"
            class="tw:mt-2 tw:rounded-md tw:border tw:border-[#edeeef] tw:bg-white tw:p-2 tw:text-xs">
            <div class="tw:mb-2">
              <div class="tw:mb-1 tw:font-medium tw:text-gray-500">Function</div>
              <code class="tw:rounded tw:bg-[#F7F7F7] tw:px-1 tw:text-gray-800">{{ functionCall.name }}</code>
            </div>
            <div class="tw:mb-2">
              <div class="tw:mb-1 tw:font-medium tw:text-gray-500">Arguments</div>
              <pre class="function-code tw:m-0 tw:overflow-auto tw:rounded tw:bg-[#F7F7F7] tw:p-2 tw:text-gray-600">{{
                JSON.stringify(functionCall.arguments, null, 2)
              }}</pre>
            </div>
            <div v-if="functionCall.result">
              <div class="tw:mb-1 tw:font-medium tw:text-gray-500">Result</div>
              <pre class="function-code tw:m-0 tw:overflow-auto tw:rounded tw:bg-[#F7F7F7] tw:p-2 tw:text-gray-600">{{
                formatResult(functionCall.result)
              }}</pre>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!isEmpty" class="tw:text-sm tw:leading-6 tw:text-gray-900">
        <span class="conversation-message-mk" v-html="html"></span><span v-if="isStreaming" class="ap-caret"></span>
      </div>
      <LeadAlert v-else-if="!isStreaming && !functionCalls.length"> Autopilot did not return a response. </LeadAlert>
    </div>
  </ConversationMessageAssistantRenderless>
</template>

<script setup lang="ts">
import { ConversationMessageAssistant as ConversationMessageAssistantRenderless } from '@llmor/autopilot-vue';
import { translateFunctionName, formatDuration, useAutopilotStore } from '@llmor/autopilot-vue';
import type { ConversationMessage, ConversationMessageMeta, FunctionCall } from '@llmor/autopilot-core';
import { IconAlertTriangle, IconBrain, IconCheck, IconChevronDown, IconLoader2 } from '@tabler/icons-vue';
import { functionCallErrorMessage, functionCallFailed } from '@/autopilot/formatAutopilotError';
import LeadAlert from '@/components/lead-design/LeadAlert.vue';

defineProps<{
  message: ConversationMessage;
  isStreaming?: boolean;
}>();

const store = useAutopilotStore();

function isFailedCall(call: FunctionCall): boolean {
  return functionCallFailed(call, store.functionCallHistory);
}

function errorMessageFor(call: FunctionCall): string {
  return functionCallErrorMessage(call, store.functionCallHistory);
}

function thinkingDuration(meta: ConversationMessageMeta | null): string {
  const formatted = formatDuration(meta?.took);
  return formatted ? ` · ${formatted}` : '';
}
</script>

<style scoped>
.conversation-message-mk {
  overflow-x: auto;
  overflow-y: visible;
  max-width: 100%;
  word-wrap: break-word;
}

.conversation-message-mk :deep(p) {
  margin-bottom: 0.5rem;
}

.conversation-message-mk :deep(p:last-child) {
  margin-bottom: 0;
}

.conversation-message-mk :deep(pre) {
  background-color: var(--tblr-bg-surface-secondary);
  border: 1px solid var(--tblr-border-color);
  border-radius: var(--tblr-border-radius);
  padding: 0.5rem;
  margin: 0.5rem 0;
  font-size: 0.75rem;
  overflow-x: auto;
}

.conversation-message-mk :deep(code) {
  background-color: var(--tblr-bg-surface-secondary);
  border-radius: var(--tblr-border-radius-sm);
  padding: 0.125rem 0.25rem;
  font-size: 0.75rem;
}

.function-code {
  overflow-x: auto;
  overflow-y: auto;
  word-wrap: break-word;
  white-space: pre-wrap;
  font-size: 0.7rem;
  max-height: 200px;
}

.ap-caret {
  display: inline-block;
  width: 2px;
  height: 14px;
  margin-left: 1px;
  vertical-align: -2px;
  background: var(--tw-color-primary);
  animation: apblink 1s steps(1) infinite;
}

@keyframes apblink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}

.ap-spin {
  animation: apspin 1.1s linear infinite;
  transform-origin: 50% 50%;
}

@keyframes apspin {
  to {
    transform: rotate(360deg);
  }
}
</style>
